import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  Button as ButtonUi,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Modal,
  Typography,
} from "@mui/material";
import { Header } from "../Header";
import {
  Container,
  UploadForm,
  FormField,
  Title,
  StepsContainer,
  StepCard,
  StepNumber,
  StepTitle,
  StepDescription,
  Content,
  ModalBox,
  ButtonsContainer,
  MainContainer,
  StepContainer,
} from "./style";
import { UploadFile } from "@mui/icons-material";
import { apiOperation } from "../../services/api";
import { Loading } from "../Loading";
import { useNavigate } from "react-router-dom";
import { getSessionToken } from "../../utils/users";
import ErrorModal from "../ErrorModal";
import * as XLSX from "xlsx";
import { MapeamentoPlanilhaRelacionamentoCliente } from "../../models/relacionamentoCliente";
import { he } from "date-fns/locale";

const camposAssociacao: Array<{ id: keyof MapeamentoPlanilhaRelacionamentoCliente; label: string }> = [
  { id: "ColunaCnpjouCpf", label: "CPF/CNPJ" },
  { id: "ColunaNomeRazaoSocial", label: "Nome/Razão Social" },
  { id: "ColunaValorTitulo", label: "Valor Título" },
  { id: "ColunaValorLimite", label: "Valor Limite Concedido" },
  { id: "ColunaDiasAtraso", label: "Dias de Atraso" },
  { id: "ColunaPrazoMedioPagamento", label: "Prazo Médio de Pagamento" },
];

const cardsData = [
  {
    number: "1",
    title: "Faça o Upload da Planilha",
    description: "Envie o arquivo Excel em formato .xlsx contendo os dados que deseja analisar.",
  },
  {
    number: "2",
    title: "Mapeie a Planilha",
    description: "Clique em 'Mapear' e selecione a linha e a coluna correspondente da planilha para cada campo do sistema.",
  },
  {
    number: "3",
    title: "Salve os Dados",
    description: "Após o mapeamento, clique em 'Salvar' para ser redirecionado à página de Histórico Relacionamento Cliente.",
  },
  {
    number: "4",
    title: "Acompanhe os Resultados",
    description: "Aguarde o status 'Concluído' no Histórico de Relacionamento do Cliente e realize suas análises.",
  },
];

const emptyMap: MapeamentoPlanilhaRelacionamentoCliente = {
  ColunaCnpjouCpf: "",
  ColunaNomeRazaoSocial: "",
  ColunaValorTitulo: "",
  ColunaValorLimite: "",
  ColunaDiasAtraso: "",
  ColunaPrazoMedioPagamento: "",
  LinhaHeader: undefined,
};

export const ImportPlanilha = () => {
  const navigate = useNavigate();
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState("");
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessages, setErrorMessages] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [excelColumns, setExcelColumns] = useState<string[]>([]);
  const [mapModal, setMapModal] = useState(false);
  const [tmpMap, setTmpMap] = useState<MapeamentoPlanilhaRelacionamentoCliente>(emptyMap);
  const [mapeamento, setMapeamento] = useState<MapeamentoPlanilhaRelacionamentoCliente>(emptyMap);
  const [headerRowIndex, setHeaderRowIndex] = useState<number | undefined>(undefined);

  const isMapped = useMemo(() => Object.values(mapeamento).every((v) => v !== ""), [mapeamento]);

  const extractHeadersFromFile = async (file: File, rowIndex: number) => {
    setLoadingModal(true);
    setLoadingMessages("Lendo cabeçalhos da planilha...");
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { sheetRows: rowIndex });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: rowIndex - 1 });
      const headers: string[] = (jsonData[0] as []) || [];
      const orderedFixedCaseHeaders = headers
        .sort((a, b) => a.localeCompare(b))
        .map((header) => header.toUpperCase());

      setExcelColumns(orderedFixedCaseHeaders);
    } catch {
      setErrorMessages("Erro ao ler o arquivo Excel. Verifique se o formato está correto.");
      setErrorModal(true);
      setSelectedFile(null);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleFileSelection = async (target: EventTarget & HTMLInputElement) => {
    if (!target.files?.length) return;
    const file = target.files[0];
    setSelectedFile(file);
  };

  useEffect(() => {
    if (!selectedFile || !headerRowIndex) return;

    extractHeadersFromFile(selectedFile, headerRowIndex);
  }, [selectedFile, headerRowIndex]);

  const handleFinalSubmit = async () => {
    setLoadingModal(true);
    setLoadingMessages("Enviando planilha...");
    if (!selectedFile) {
      setErrorMessages("Ocorreu um erro, o arquivo original não foi encontrado.");
      setErrorModal(true);
      setLoadingModal(false);
      return;
    }
    const formData = new FormData();
    formData.append("planilha", selectedFile);
    Object.entries(mapeamento).forEach(([key, value]) => {
      formData.append(`mapeamento.${key}`, value);
    });
    try {
      await apiOperation.post("relacionamento-cliente/importacao/planilha", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${getSessionToken()}`,
        },
      });
      setLoadingModal(false);
      navigate("/relacionamento-cliente");
    } catch (error: any) {
      setLoadingModal(false);
      setErrorMessages(`${error.code}\nAtualize a página e tente novamente!`);
      setErrorModal(true);
    }
  };

  const handleOpenMap = () => {
    setTmpMap(emptyMap);
    setMapeamento(emptyMap);
    setMapModal(true);
  };

  const handleCloseMap = () => {
    setTmpMap(emptyMap);
    setMapeamento(emptyMap);
    setMapModal(false);
  };

  const handleMapChange = (field: keyof MapeamentoPlanilhaRelacionamentoCliente, value: string | number) => {
    setTmpMap((prev) => ({ ...prev, [field]: value }));
  };

  const handleMapApply = () => {
    setMapeamento(tmpMap);
    setTmpMap(emptyMap);
    setMapModal(false);
  };

  return (
    <>
      <Header />
      <Loading mode="blurry" hidden={!loadingModal} messages={loadingMessages} />
      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        errorMsg={errorMessages}
        title="Atenção!"
        onExit={() => {}}
        continuarAnalise={() => {}}
      />
      <Modal open={mapModal} onClose={handleCloseMap}>
        <ModalBox>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Configurar Mapeamento de Colunas
          </Typography>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="header-row-label">Linha do Cabeçalho</InputLabel>
            <Select
              labelId="header-row-label"
              value={headerRowIndex}
              label="Linha do Cabeçalho"
              onChange={(e) => { 
                handleMapChange("LinhaHeader", e.target.value as number);
                setHeaderRowIndex(e.target.value as number);
              }}
            >
              {Array.from({ length: 20 }, (_, i) => (
                <MenuItem key={i} value={i + 1}>
                  Linha {i + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Grid container spacing={3} sx={{ marginBottom: "2rem" }}>
            {camposAssociacao.map((campo) => (
              <Grid item xs={12} key={campo.id}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel id={`label-${campo.id}`}>{campo.label}</InputLabel>
                  <Select
                    id={`select-${campo.id}`}
                    label={campo.label}
                    value={tmpMap[campo.id]}
                    onChange={(e) => handleMapChange(campo.id, e.target.value)}
                  >
                    {(excelColumns || [])
                      .filter((col) => col === tmpMap[campo.id] || !Object.values(tmpMap).includes(col))
                      .map((col) => (
                        <MenuItem key={col} value={col}>
                          {col}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={handleCloseMap}>
              Cancelar
            </Button>
            <ButtonUi variant="contained" color="secondary" onClick={handleMapApply}>
              Aplicar Mapeamento
            </ButtonUi>
          </Box>
        </ModalBox>
      </Modal>
      <Container>
        <MainContainer>
          <StepContainer>
            <Box>
              <Content>
                <Title>Classificação Relacionamento Do Cliente</Title>
              </Content>
              <Card sx={{ padding: "20px" }}>
                <CardContent style={{ padding: "0" }}>
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <UploadForm style={{ width: "fit-content" }}>
                      <FormField>
                        <label htmlFor="contained-arquivo">
                          <ButtonUi
                            variant="outlined"
                            component="div"
                            endIcon={<UploadFile />}
                            sx={{ borderStyle: "dashed", borderRadius: "15px", textTransform: "none" }}
                          >
                            {selectedFile?.name || "SELECIONE SEU ARQUIVO EXCEL"}
                            <input
                              accept=".xlsx"
                              id="contained-arquivo"
                              type="file"
                              hidden
                              onChange={({ target }) => handleFileSelection(target)}
                            />
                          </ButtonUi>
                        </label>
                      </FormField>
                    </UploadForm>
                    <Box sx={{ ml: "auto" }} />
                    <ButtonsContainer>
                      <ButtonUi
                        disabled={!selectedFile}
                        variant="contained"
                        color="primary"
                        onClick={handleOpenMap}
                        sx={{ width: "10.4em" }}
                      >
                        Mapear
                      </ButtonUi>
                      <ButtonUi
                        disabled={!selectedFile || !isMapped}
                        variant="contained"
                        color="secondary"
                        onClick={handleFinalSubmit}
                        sx={{ width: "10.4em" }}
                      >
                        Salvar
                      </ButtonUi>
                    </ButtonsContainer>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box>
              <Content>
                <Title>Como funciona a Classificação Relacionamento do Cliente?</Title>
              </Content>
              <Card sx={{ padding: "20px" }}>
                <CardContent style={{ padding: "0" }}>
                  <StepsContainer>
                    {cardsData.map((card, index) => (
                      <StepCard key={index}>
                        <StepNumber>{card.number}</StepNumber>
                        <StepTitle>{card.title}</StepTitle>
                        <StepDescription>{card.description}</StepDescription>
                      </StepCard>
                    ))}
                  </StepsContainer>
                </CardContent>
              </Card>
            </Box>
          </StepContainer>
        </MainContainer>
      </Container>
    </>
  );
};
